// admin/events/EventApprovalModal.jsx — now multi-purpose EventActionModal
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  sponsorListEvent,
  publishAdminEvent,
  archiveAdminEvent,
  editAdminEvent,
  hideAdminEvent,
  unhideAdminEvent,
  transitionAdminEvent,
} from "@/store/slices/adminSlice";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "react-toastify";
import { ListChecks, Globe, Archive, Pencil, EyeOff, ArrowLeft, ArrowRight } from "lucide-react";

// Valid transitions per status
const TRANSITIONS = {
  submitted: {
    forward: [
      { key: "sponsor_listed", label: "Move to Sponsor Listed", icon: <ListChecks className="w-5 h-5" />, tone: "text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50" },
      { key: "published", label: "Publish", icon: <Globe className="w-5 h-5" />, tone: "text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50" },
      { key: "archived", label: "Archive", icon: <Archive className="w-5 h-5" />, tone: "text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600" },
    ],
    backward: []
  },
  sponsor_listed: {
    forward: [
      { key: "published", label: "Publish", icon: <Globe className="w-5 h-5" />, tone: "text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50" },
      { key: "hidden", label: "Hide Event", icon: <EyeOff className="w-5 h-5" />, tone: "text-purple-700 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50" },
      { key: "archived", label: "Archive", icon: <Archive className="w-5 h-5" />, tone: "text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600" },
    ],
    backward: [
      { key: "submitted", label: "Revert to Submitted", icon: <ArrowLeft className="w-5 h-5" />, tone: "text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50" }
    ]
  },
  published: {
    forward: [
      { key: "hidden", label: "Hide Event", icon: <EyeOff className="w-5 h-5" />, tone: "text-purple-700 bg-purple-50 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50" },
      { key: "archived", label: "Archive", icon: <Archive className="w-5 h-5" />, tone: "text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600" },
    ],
    backward: [
      { key: "sponsor_listed", label: "Revert to Sponsor Listed", icon: <ArrowLeft className="w-5 h-5" />, tone: "text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50" },
    ]
  },
  hidden: {
    forward: [
      { key: "published", label: "Publish (Unhide)", icon: <Globe className="w-5 h-5" />, tone: "text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50" },
      { key: "archived", label: "Archive", icon: <Archive className="w-5 h-5" />, tone: "text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600" },
    ],
    backward: [
      { key: "sponsor_listed", label: "Revert to Sponsor Listed", icon: <ArrowLeft className="w-5 h-5" />, tone: "text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50" },
      { key: "submitted", label: "Revert to Submitted", icon: <ArrowLeft className="w-5 h-5" />, tone: "text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50" },
    ]
  },
  archived: {
    forward: [],
    backward: [
      { key: "published", label: "Publish (Un-archive)", icon: <Globe className="w-5 h-5" />, tone: "text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50", requiresReason: true },
      { key: "submitted", label: "Revert to Submitted", icon: <ArrowLeft className="w-5 h-5" />, tone: "text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/50", requiresReason: true }
    ]
  }
};

const EVENT_TYPES = [
  { value: "cycle_tour", label: "Cycle Tour" },
  { value: "cycle_kumbh", label: "Cycle Kumbh" },
  { value: "flash_mob", label: "Flash Mob" },
  { value: "koset_ride", label: "Koset Ride" },
];

// Convert UTC ISO string to a local datetime-local string (e.g. "2026-06-25T14:49")
// This ensures the datetime-local input renders in the user's local timezone
// so a round-trip read-back does not shift the time.
const toLocalDatetimeInput = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  // Offset to local time and format as YYYY-MM-DDTHH:mm
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d - offset).toISOString().slice(0, 16);
};

const EventActionModal = ({ event, onClose, onActionComplete }) => {
  const dispatch = useDispatch();
  const { isUpdating } = useSelector((state) => state.admin);

  const [mode, setMode] = useState("actions"); // "actions" | "edit"
  const [confirmAction, setConfirmAction] = useState(null); // { key, label }

  // Store original date strings (as local input values) to detect if user actually changed them
  const originalDates = {
    start_date: toLocalDatetimeInput(event.start_date),
    end_date: toLocalDatetimeInput(event.end_date),
  };

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: event.title || "",
    description: event.description || "",
    capacity: event.capacity || 0,
    event_type: event.event_type || "",
    start_date: originalDates.start_date,
    end_date: originalDates.end_date,
  });

  const [transitionReason, setTransitionReason] = useState("");

  const transitions = TRANSITIONS[event.status] || { forward: [], backward: [] };

  const handleTransitionConfirm = async () => {
    if (!confirmAction) return;

    if (confirmAction.requiresReason && !transitionReason.trim()) {
      toast.error("A reason is required for this action");
      return;
    }

    try {
      let action;
      if (confirmAction.key === "sponsor_listed") {
         action = (event.status === "submitted") ? sponsorListEvent(event._id) : transitionAdminEvent({ eventId: event._id, status: "sponsor_listed", reason: transitionReason });
      }
      else if (confirmAction.key === "published") {
          action = event.status === "hidden" ? unhideAdminEvent(event._id) : 
                   event.status === "archived" ? transitionAdminEvent({ eventId: event._id, status: "published", reason: transitionReason }) :
                   publishAdminEvent(event._id);
      }
      else if (confirmAction.key === "archived") action = archiveAdminEvent(event._id);
      else if (confirmAction.key === "submitted") action = transitionAdminEvent({ eventId: event._id, status: "submitted", reason: transitionReason });
      else if (confirmAction.key === "hidden") action = hideAdminEvent(event._id);

      if (action) {
        const result = await dispatch(action).unwrap();
        toast.success(result.message || "Event updated successfully");
        onActionComplete();
      } else {
        toast.error("Invalid action specified");
      }
    } catch (err) {
      console.error("Action failed:", err);
      toast.error(err.message || "Failed to update event status");
    }
    setConfirmAction(null);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        title: editForm.title,
        description: editForm.description,
        capacity: Number(editForm.capacity),
        event_type: editForm.event_type,
      };

      // Only include dates if the user actually changed them from the original value.
      // Converting via `new Date(localInputValue)` is safe here because the input value
      // was populated from toLocalDatetimeInput(), so the browser interprets it as local time
      // and new Date() also parses it as local time — no timezone shift occurs.
      if (editForm.start_date && editForm.start_date !== originalDates.start_date) {
        updates.start_date = new Date(editForm.start_date).toISOString();
      }
      if (editForm.end_date && editForm.end_date !== originalDates.end_date) {
        updates.end_date = new Date(editForm.end_date).toISOString();
      }

      const result = await dispatch(editAdminEvent({ eventId: event._id, updates })).unwrap();
      toast.success(result.message || "Event updated successfully");
      onActionComplete();
    } catch (err) {
      // Error toast is handled by the API interceptor
    }
  };


  return (
    <>
      <Modal
        open={true}
        onClose={onClose}
        title={mode === "edit" ? "Edit Event" : `Actions — ${event.title}`}
        maxWidth="max-w-xl"
        showCloseButton={false}
        zIndex="z-[60]"
      >
        {mode === "actions" ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Current status: <span className="font-medium text-gray-800 dark:text-gray-100 capitalize">{event.status?.replace(/_/g, " ")}</span>
            </p>

            {/* Status Transitions */}
            {transitions.forward?.length > 0 && (
              <div className="space-y-2 pb-2">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-green-600 dark:text-green-400 mb-1 flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" /> Advance
                </p>
                {transitions.forward.map((t) => (
                  <button
                    key={`fwd-${t.key}`}
                    onClick={() => { setConfirmAction(t); setTransitionReason(""); }}
                    disabled={isUpdating}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${t.tone} ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {transitions.backward?.length > 0 && (
              <div className="space-y-2 pb-2">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Backtrack / Options
                </p>
                {transitions.backward.map((t) => (
                  <button
                    key={`bwd-${t.key}`}
                    onClick={() => { setConfirmAction(t); setTransitionReason(""); }}
                    disabled={isUpdating}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${t.tone} ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {t.icon}
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {!transitions.forward?.length && !transitions.backward?.length && (
              <p className="text-sm text-gray-500 italic">No state transitions available for this event.</p>
            )}

            {/* Edit button */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <button
                onClick={() => setMode("edit")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Edit Event Details
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                minLength={5}
                maxLength={200}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                minLength={20}
                maxLength={2000}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Type</label>
                <select
                  value={editForm.event_type}
                  onChange={(e) => setEditForm((f) => ({ ...f, event_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Capacity</label>
                <input
                  type="number"
                  value={editForm.capacity}
                  onChange={(e) => setEditForm((f) => ({ ...f, capacity: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  min={0}
                  placeholder="0 = unlimited"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                <input
                  type="datetime-local"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                <input
                  type="datetime-local"
                  value={editForm.end_date}
                  onChange={(e) => setEditForm((f) => ({ ...f, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" size="sm" type="button" onClick={() => setMode("actions")}>
                Back
              </Button>
              <Button variant="primary" size="sm" type="submit" loading={isUpdating} disabled={isUpdating}>
                Save Changes
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title={`Confirm: ${confirmAction?.label}`}
        message={
            confirmAction?.requiresReason 
                ? `Are you sure you want to "${confirmAction?.label}" for this event? A reason must be provided.`
                : `Are you sure you want to "${confirmAction?.label}" for this event? This action cannot be undone.`
        }
        confirmLabel={confirmAction?.requiresReason ? "Proceed" : "Confirm"}
        confirmVariant={confirmAction?.requiresReason ? "primary" : "danger"}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleTransitionConfirm}
        isLoading={isUpdating}
      >
        {confirmAction?.requiresReason && (
            <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Action <span className="text-red-500">*</span></label>
                <textarea
                    value={transitionReason}
                    onChange={(e) => setTransitionReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="Enter a descriptive reason..."
                    required
                />
            </div>
        )}
      </ConfirmDialog>
    </>
  );
};

export default EventActionModal;
